import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useScrollAnimation } from "../../../utils/Utils";

const consultingServicesConfig = {
  ai_ml: {
    path: "ai_ml",
    title: "AI & ML",
    navLabel: "AI & ML",
    headline: "Artificial Intelligence & Machine Learning Consulting",
    description:
      "Harness intelligent automation, predictive analytics, and custom ML models to transform operations, improve decision-making, and unlock new business value.",
    metaDescription:
      "PT KCS delivers AI & ML consulting in Indonesia — strategy, model development, MLOps, and enterprise integration for scalable intelligent solutions.",
    capabilities: [
      "AI strategy & roadmap design",
      "Predictive analytics & forecasting",
      "Custom ML model development",
      "MLOps & production deployment",
    ],
  },
  cybersecurity: {
    path: "cybersecurity",
    title: "Cybersecurity",
    navLabel: "Cybersecurity",
    headline: "Enterprise Cybersecurity Consulting & Protection",
    description:
      "Strengthen your security posture with risk assessments, compliance guidance, threat detection, and resilient architectures built for modern enterprise environments.",
    metaDescription:
      "PT KCS cybersecurity services — risk assessment, compliance, security architecture, and managed protection for enterprises in Indonesia.",
    capabilities: [
      "Security assessments & audits",
      "Compliance & governance (ISO, SOC)",
      "Identity & access management",
      "Incident response planning",
    ],
  },
  agentic_ai: {
    path: "agentic_ai",
    title: "Agentic AI",
    navLabel: "Agentic AI",
    headline: "Agentic AI Solutions for Autonomous Workflows",
    description:
      "Deploy intelligent agents that plan, reason, and execute multi-step tasks across your systems — automating complex workflows with human oversight where it matters.",
    metaDescription:
      "PT KCS Agentic AI consulting — autonomous agents, workflow automation, and enterprise AI orchestration for smarter business operations.",
    capabilities: [
      "Autonomous agent design",
      "Multi-system workflow orchestration",
      "Tool & API integration",
      "Governance & human-in-the-loop controls",
    ],
  },
  generative_ai: {
    path: "generative_ai",
    title: "Generative AI",
    navLabel: "Generative AI",
    headline: "Generative AI for Content, Code & Customer Experience",
    description:
      "Implement secure generative AI for knowledge assistants, document automation, code generation, and personalized customer experiences aligned with your brand and policies.",
    metaDescription:
      "PT KCS Generative AI services — LLM integration, RAG, enterprise copilots, and secure GenAI deployment for Indonesian businesses.",
    capabilities: [
      "Enterprise copilots & assistants",
      "RAG & knowledge base integration",
      "Content & document automation",
      "Responsible AI & data privacy",
    ],
  },
};

const ConsultingServicePage = ({ serviceKey }) => {
  const service = consultingServicesConfig[serviceKey];

  useScrollAnimation();

  if (!service) {
    return null;
  }

  return (
    <div>
      <Helmet>
        <title>PT KCS | {service.title} Consulting Services Indonesia</title>
        <meta name="description" content={service.metaDescription} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Hero */}
      <section className="bg-[#1a1a1a] text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link to="/services/infor" className="hover:text-white transition-colors">
              Consulting Services
            </Link>
            <span>/</span>
            <span className="text-white/90">{service.title}</span>
          </nav>
          <p className="text-[12px] font-semibold tracking-[0.12em] uppercase text-[#FF4A3D] mb-4">
            Consulting Services
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold max-w-3xl leading-tight mb-6">
            {service.headline}
          </h1>
          <p className="text-lg text-white/75 max-w-2xl leading-relaxed">
            {service.description}
          </p>
          <Link
            to="/contact"
            className="inline-flex mt-10 px-8 py-3.5 bg-white text-[#1a1a1a] font-medium rounded-full hover:bg-[#f5f3f1] transition-colors duration-200"
          >
            Talk to our experts
          </Link>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-3">
            What we deliver
          </h2>
          <p className="text-[#686564] max-w-2xl mb-10">
            Our {service.title} practice is being expanded. Connect with PT KCS to discuss your
            requirements and roadmap.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {service.capabilities.map((item) => (
              <div
                key={item}
                className="p-6 rounded-2xl border border-[#e8e5e3] bg-[#faf9f8] hover:border-[#FF4A3D]/30 hover:shadow-sm transition-all duration-200"
              >
                <div className="w-2 h-2 rounded-full bg-[#FF4A3D] mb-4" />
                <p className="text-[15px] font-medium text-[#2a2827] leading-snug">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#f5f3f1] border-t border-[#e8e5e3]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-[#1a1a1a]">
              Ready to build your {service.title} capability?
            </h2>
            <p className="text-[#686564] mt-2">
              We are developing full service pages — reach out today to start the conversation.
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex justify-center px-8 py-3.5 bg-[#1a1a1a] text-white font-medium rounded-full hover:bg-[#2a2827] transition-colors duration-200 whitespace-nowrap"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ConsultingServicePage;
